import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, User, Gift, ArrowLeftRight, Pencil, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_LABELS, STATUS_BADGE } from "../lib/constants";
import Seo, { SITE, DEFAULT_OG } from "../components/Seo";
import BookCover from "../components/BookCover";
import SafetyBanner from "../components/SafetyBanner";
import ReportDialog from "../components/ReportDialog";
import ShareMenu from "../components/ShareMenu";
import QRDialog from "../components/QRDialog";
import { RatingLabel } from "../components/Stars";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [myBooks, setMyBooks] = useState([]);
  const [offerChoice, setOfferChoice] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${id}`);
      setBook(data);
    } catch { toast.error("Libro non trovato"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openPropose = async () => {
    if (!user) { navigate("/accedi"); return; }
    try {
      const { data } = await api.get("/books/mine");
      setMyBooks(data.filter((b) => b.status === "available"));
    } catch {}
    setOfferChoice(book.is_gift ? "gift" : "");
    setMessage("");
    setOpen(true);
  };

  const submitPropose = async () => {
    setSending(true);
    try {
      const payload = { book_id: book.id, message };
      payload.offered_book_id = offerChoice === "gift" ? null : offerChoice;
      const { data } = await api.post("/proposals", payload);
      toast.success("Proposta inviata!");
      setOpen(false);
      navigate(`/proposta/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-20 text-center text-muted-foreground">Caricamento…</div>;
  if (!book) return <div className="max-w-5xl mx-auto px-4 py-20 text-center">Libro non trovato.</div>;

  const isOwner = user && book.owner_id === user.id;
  const canPropose = !book.is_demo && !isOwner && ["available", "in_negotiation"].includes(book.status);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `«${book.title}» di ${book.author} su Libri in Circolo: ${shareUrl}`;
  const cover = book.cover_url || (book.photo_path ? fileUrl(book.photo_path) : DEFAULT_OG);
  const indexable = ["available", "in_negotiation"].includes(book.status) && !book.is_demo;
  const seoTitle = `${book.title} di ${book.author} – da scambiare a ${book.comune} | Libri in Circolo`;
  const seoDesc = `${book.title} di ${book.author}, ${book.condition}, disponibile a ${book.comune}. Scambia o ricevi gratis questo libro usato su Libri in Circolo.`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/libro/${book.id}`}
        image={cover}
        type="article"
        noindex={!indexable}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Book",
            name: book.title,
            author: { "@type": "Person", name: book.author },
            ...(book.isbn ? { isbn: book.isbn } : {}),
            image: cover,
            inLanguage: "it",
            url: `${SITE}/libro/${book.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Catalogo", item: SITE + "/" },
              { "@type": "ListItem", position: 2, name: book.title, item: `${SITE}/libro/${book.id}` },
            ],
          },
        ]}
      />
      {!indexable && !book.is_demo && (
        <div data-testid="book-unavailable-notice" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm">
          Questo libro non è più disponibile. <Link to="/" className="font-semibold underline">Torna al catalogo</Link> per scoprire gli altri libri.
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-2" />Torna al catalogo</Button>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,340px)_1fr] gap-8">
        <div className="min-w-0">
          <div className="aspect-[3/4] rounded-2xl border border-border bg-muted overflow-hidden sticky top-20">
            <BookCover book={book} />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="outline" className={STATUS_BADGE[book.status]}>{STATUS_LABELS[book.status]}</Badge>
            {book.is_gift && <Badge className="bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300"><Gift className="w-3 h-3 mr-1" />In regalo</Badge>}
            {book.is_demo && <Badge className="bg-stone-900/80 text-white border-0 font-mono text-[10px] uppercase tracking-wider">Annuncio di esempio</Badge>}
          </div>

          <h1 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight leading-tight break-words">{book.title}</h1>
          <p className="text-lg text-muted-foreground mt-1 break-words">di {book.author}</p>

          <div className="flex flex-wrap gap-2 mt-5">
            <Badge variant="secondary">{book.genre}</Badge>
            <Badge variant="outline">{book.condition}</Badge>
            <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" />{book.comune}</Badge>
            {book.year && <Badge variant="outline" data-testid="book-year-badge">{book.year}</Badge>}
          </div>

          {book.description && <p className="mt-6 text-base leading-relaxed whitespace-pre-line">{book.description}</p>}
          {book.isbn && <p className="mt-4 text-sm text-muted-foreground font-mono">ISBN: {book.isbn}</p>}

          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><User className="w-4 h-4" />Proposto da {book.is_demo ? <strong className="text-foreground">{book.owner?.name}</strong> : <Link to={`/utente/${book.owner?.id}`} data-testid="owner-profile-link" className="text-foreground font-semibold hover:text-primary hover:underline">{book.owner?.name}</Link>} · {book.owner?.comune}</span>
            {book.owner?.rating && <RatingLabel rating={book.owner.rating} className="ml-1" />}
          </div>

          {book.is_demo && (
            <div className="mt-6 flex items-start gap-2 rounded-xl border border-border bg-muted px-4 py-3 text-sm">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              Questo è un annuncio dimostrativo, utile solo a mostrare come appare il catalogo. Non è possibile proporne lo scambio.
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {isOwner ? (
              <Button asChild className="bg-primary hover:bg-primary/90"><Link to={`/modifica/${book.id}`} data-testid="edit-book-button"><Pencil className="w-4 h-4 mr-2" />Modifica annuncio</Link></Button>
            ) : canPropose ? (
              <Button data-testid="propose-button" size="lg" className="bg-primary hover:bg-primary/90" onClick={openPropose}>
                <ArrowLeftRight className="w-4 h-4 mr-2" />Proponi uno scambio
              </Button>
            ) : !book.is_demo && (
              <Badge variant="outline" className="text-sm py-1.5">Non disponibile per lo scambio</Badge>
            )}
            {user && !isOwner && !book.is_demo && <ReportDialog targetType="book" targetId={book.id} label="Segnala annuncio" />}
            <ShareMenu url={shareUrl} text={shareText} />
            <QRDialog url={shareUrl} title={book.title} />
          </div>

          <SafetyBanner className="mt-8" />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">Proponi uno scambio per «{book.title}»</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Cosa offri in cambio?</Label>
              <RadioGroup value={offerChoice} onValueChange={setOfferChoice} className="space-y-2">
                {book.is_gift && (
                  <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-muted">
                    <RadioGroupItem value="gift" data-testid="offer-gift" />
                    <span className="flex items-center gap-2 text-sm"><Gift className="w-4 h-4 text-rose-600" />Richiedilo in regalo (senza dare nulla in cambio)</span>
                  </label>
                )}
                {myBooks.length === 0 && !book.is_gift && (
                  <p className="text-sm text-muted-foreground">Non hai libri disponibili da offrire. <Link to="/pubblica" className="text-primary underline">Pubblicane uno</Link>.</p>
                )}
                {myBooks.map((b) => (
                  <label key={b.id} className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-muted">
                    <RadioGroupItem value={b.id} data-testid={`offer-book-${b.id}`} />
                    <span className="text-sm"><strong>{b.title}</strong> — {b.author}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="msg" className="mb-1.5 block">Messaggio (facoltativo)</Label>
              <Textarea id="msg" data-testid="propose-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Presentati e proponi come organizzarvi…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="propose-submit" onClick={submitPropose} disabled={sending || !offerChoice} className="bg-primary hover:bg-primary/90">Invia proposta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
